import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import ReCAPTCHA from 'react-google-recaptcha';
import { Copy } from '@styled-icons/fa-solid/Copy';
import { toast } from 'react-toastify';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { API_URL, MINIMUM_AGE } from '../../constants/string-constants';
import { device } from '../../constants/device-sizes';
import { getSiteName, getSiteSubtitle } from '../../hooks/useSiteConfig';
import {
  ModalWrapper,
  ModalContent,
  ModernTitle,
  ModernSubtitle,
  ModernButton,
} from './shared/ModalBase';

const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const CaptchaCaption = styled.p`
  color: var(--clr-sec);
  font-size: 0.85rem;
  text-align: center;
  margin-bottom: 0.75rem;
  opacity: 0.8;
`;

const RulesSection = styled.div`
  background: rgba(102, 126, 234, 0.05);
  border-radius: 12px;
  padding: 1rem;
  margin: 1.5rem 0;
  border: 1px solid rgba(102, 126, 234, 0.15);
`;

const RulesHeader = styled.h3`
  color: var(--clr-text);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &::before {
    content: '📜';
    font-size: 1rem;
  }
`;

const RulesScrollContainer = styled.div`
  max-height: 180px;
  overflow-y: auto;
  padding-right: 0.5rem;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(102, 126, 234, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.4);
    border-radius: 3px;

    &:hover {
      background: rgba(102, 126, 234, 0.6);
    }
  }
`;

const RulesList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  counter-reset: rule-counter;
`;

const RuleItem = styled.li`
  counter-increment: rule-counter;
  position: relative;
  padding-left: 2rem;
  margin-bottom: 0.75rem;
  color: var(--clr-sec);
  line-height: 1.4;
  font-size: 0.85rem;

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: counter(rule-counter);
    position: absolute;
    left: 0;
    top: 0;
    width: 1.4rem;
    height: 1.4rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.7rem;
  }

  strong {
    color: var(--clr-text);
    font-weight: 600;
  }
`;

const AgeRequirement = styled.div`
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%);
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 600;
  color: var(--clr-text);
  font-size: 0.9rem;
`;

const RulesFooter = styled.p`
  color: var(--clr-sec);
  font-size: 0.8rem;
  text-align: center;
  margin-top: 0.75rem;
  opacity: 0.8;
  font-style: italic;
`;

const CaptchaContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const VerifyButton = styled(ModernButton)`
  min-width: 200px;

  @media ${device.tablet} {
    min-width: 250px;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  text-align: center;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: rgba(255, 107, 107, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(255, 107, 107, 0.2);
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const BanAppealSection = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(255, 107, 107, 0.2);
`;

const AppealTitle = styled.h3`
  color: var(--clr-text);
  margin: 0 0 1rem 0;
  text-align: center;
  font-size: 1.1rem;
`;

const AppealDescription = styled.p`
  color: var(--clr-text-secondary);
  margin-bottom: 1rem;
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const ContactMethod = styled.div`
  background-color: var(--clr-prim);
  border: 1px solid color-mix(in srgb, var(--clr-sec) 30%, transparent);
  border-radius: 0.75rem;
  padding: 0.75rem;
  margin: 0.5rem 0;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: var(--clr-text);
  font-weight: 500;
  font-size: 0.9rem;

  &:hover {
    transform: translateY(-1px);
    background-color: var(--clr-hover);
  }
`;

const CopyIcon = styled(Copy)`
  width: 0.9rem;
  height: 0.9rem;
  color: var(--clr-text);
`;

const DiscordButton = styled.div`
  background-color: var(--clr-prim);
  border: 1px solid color-mix(in srgb, var(--clr-sec) 30%, transparent);
  border-radius: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  color: var(--clr-text);
  font-weight: 500;
  font-size: 0.9rem;
  margin: 0.5rem 0;

  &:hover {
    transform: translateY(-1px);
    background-color: var(--clr-hover);
  }

  a {
    color: var(--clr-text);
    text-decoration: none;
    display: block;
    margin: -0.75rem;
    padding: 0.75rem;
  }
`;

function CaptchaVerificationModal({ visible, onSuccess }) {
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [banDetails, setBanDetails] = useState(null);

  // Check if the error is a ban message (but not a VPN ban)
  const isBanned =
    error.includes('You are currently banned') && !error.includes('VPN');
  const isVpnBanned = banDetails?.error === 'VPN_BLOCKED';

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      const analytics = getAnalytics();
      logEvent(analytics, 'contact_copied', {
        type: type,
        referrer: 'captcha_ban_appeal',
      });
      toast.success('Copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Failed to copy');
    }
  };

  const handleDiscordJoin = () => {
    const analytics = getAnalytics();
    logEvent(analytics, 'discord_clicked', {
      referrer: 'captcha_ban_appeal',
    });
  };

  const handleCaptchaChange = useCallback((token) => {
    setCaptchaToken(token);
    setError('');
  }, []);

  const handleCaptchaExpired = useCallback(() => {
    setCaptchaToken(null);
    setError('reCAPTCHA expired. Please try again.');
  }, []);

  const handleCaptchaError = useCallback(() => {
    setCaptchaToken(null);
    setError(
      'reCAPTCHA failed to load. Please refresh the page and try again.'
    );
  }, []);

  const handleVerify = async () => {
    // Skip captcha in local development
    const isLocalDev =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    if (!captchaToken && !isLocalDev) {
      setError('Please complete the reCAPTCHA challenge first.');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      // Skip API call in local development
      if (isLocalDev) {
        // Generate a fake session token for local dev
        const fakeSessionToken = 'local-dev-session-' + Date.now();
        sessionStorage.setItem('captcha_session_token', fakeSessionToken);
        onSuccess(fakeSessionToken);
        return;
      }

      const response = await fetch(API_URL + 'verify-captcha-websocket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          captchaToken: captchaToken,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Store the session token for WebSocket authentication
        sessionStorage.setItem('captcha_session_token', result.sessionToken);
        onSuccess(result.sessionToken);
      } else {
        setError(result.message || 'Verification failed. Please try again.');
        setBanDetails(result); // Store the full response for ban details
        setCaptchaToken(null); // Reset captcha on error
      }
    } catch (error) {
      console.error('Captcha verification error:', error);
      setError('Network error. Please check your connection and try again.');
      setCaptchaToken(null);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Modal
      isOpen={visible}
      onRequestClose={() => {}} // Prevent closing on overlay click since this is required
      style={{
        overlay: {
          zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(8px)',
        },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <WelcomeSection>
            <ModernTitle>Welcome to {getSiteName()}!</ModernTitle>
            <ModernSubtitle>
              {getSiteSubtitle()}
            </ModernSubtitle>
          </WelcomeSection>

          <RulesSection>
            <RulesHeader>Community Rules</RulesHeader>
            <RulesScrollContainer>
              <AgeRequirement>
                You must be {MINIMUM_AGE} years or older to use {getSiteName()}.
              </AgeRequirement>
              <RulesList>
                <RuleItem>
                  <strong>Be respectful:</strong> Treat others with kindness and respect.
                </RuleItem>
                <RuleItem>
                  <strong>Not a substitute for professional help:</strong> {getSiteName()} allows venting and emotional support, but it isn't a substitute for professional help. If you are experiencing an active mental health crisis/are at immediate risk of harm, please contact a crisis hotline or emergency services.
                </RuleItem>
                <RuleItem>
                  <strong>No hate speech:</strong> Discrimination or harassment based on any characteristic is prohibited.
                </RuleItem>
                <RuleItem>
                  <strong>No threats or violence:</strong> Threats of violence or harm result in immediate banning.
                </RuleItem>
                <RuleItem>
                  <strong>No illegal content:</strong> Do not share or promote illegal activities.
                </RuleItem>
                <RuleItem>
                  <strong>No spam or advertising:</strong> Keep conversations genuine.
                </RuleItem>
                <RuleItem>
                  <strong>Protect privacy:</strong> Never share personal information about yourself or others.
                </RuleItem>
                <RuleItem>
                  <strong>Keep intimacy private:</strong> No sexual comments towards minors.
                </RuleItem>
                <RuleItem>
                  <strong>Report violations:</strong> Help us keep the community safe.
                </RuleItem>
              </RulesList>
            </RulesScrollContainer>
            <RulesFooter>
              By continuing, you agree to follow these rules.
            </RulesFooter>
          </RulesSection>

          {isVpnBanned ? (
            <BanAppealSection>
              <AppealTitle>VPN Detected</AppealTitle>
              <AppealDescription>
                We've detected that you're using a VPN or proxy service. VPNs are currently blocked because trolls use them to evade bans.
              </AppealDescription>
              <AppealDescription style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                Please disconnect your VPN to use {getSiteName()}. Note: iCloud Private Relay is also considered a VPN.
              </AppealDescription>
              {banDetails?.banId && (
                <AppealDescription
                  style={{
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    fontSize: '0.85rem',
                    opacity: 0.8
                  }}
                >
                  Ban ID: #{banDetails.banId}
                </AppealDescription>
              )}
              <AppealDescription style={{ marginTop: '1rem' }}>
                If you're not using a VPN and believe this is an error, please join our Discord server and post in #ban-appeals
                {banDetails?.banId ? ` with your ban ID (#${banDetails.banId})` : ''}.
              </AppealDescription>
              <DiscordButton
                onClick={handleDiscordJoin}
                style={{
                  marginBottom: '0.5rem',
                  backgroundColor: '#5865F2',
                  borderColor: '#5865F2',
                }}
              >
                <a
                  href='https://discord.gg/rM7g5Zusmr'
                  target='_blank'
                  rel='noreferrer noopener'
                  style={{ color: 'white', fontWeight: '600' }}
                >
                  Appeal by joining the {getSiteName()} Discord
                </a>
              </DiscordButton>
              <AppealDescription style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
                Or email us for ban appeals (responses may be slower):
              </AppealDescription>
              <ContactMethod onClick={() => copyToClipboard('officialmodsofvs@gmail.com', 'email')}>
                <CopyIcon />
                officialmodsofvs@gmail.com
              </ContactMethod>
            </BanAppealSection>
          ) : isBanned ? (
            <BanAppealSection>
              <AppealTitle>You are currently banned</AppealTitle>
              {banDetails?.banId && (
                <AppealDescription
                  style={{ 
                    fontWeight: 600, 
                    marginBottom: '0.5rem',
                    fontSize: '0.85rem',
                    opacity: 0.8
                  }}
                >
                  Ban ID: #{banDetails.banId}
                </AppealDescription>
              )}
              {banDetails?.unbanDate && (
                <AppealDescription
                  style={{ fontWeight: 600, marginBottom: '0.5rem' }}
                >
                  Your ban will be lifted on:{' '}
                  {new Date(banDetails.unbanDate).toLocaleDateString()} at{' '}
                  {new Date(banDetails.unbanDate).toLocaleTimeString()}
                </AppealDescription>
              )}
              {banDetails?.externalReason && (
                <AppealDescription
                  style={{
                    fontWeight: 600,
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    backgroundColor: 'rgba(255, 107, 107, 0.15)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 107, 107, 0.3)',
                  }}
                >
                  Reason: {banDetails.externalReason}
                </AppealDescription>
              )}
              {banDetails?.bannedMessage && (
                <AppealDescription
                  style={{
                    fontWeight: 600,
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    backgroundColor: 'rgba(255, 107, 107, 0.15)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 107, 107, 0.3)',
                  }}
                >
                  Banned Message: "{banDetails.bannedMessage}"
                </AppealDescription>
              )}
              <AppealDescription>
                If you believe this ban was issued in error or would like to
                appeal, please join our Discord server and post in #ban-appeals
                with your ban ID {banDetails?.banId ? ` (#${banDetails.banId})` : ''}
                where all moderators can review your case and provide context on
                the ban.
              </AppealDescription>

              <DiscordButton
                onClick={handleDiscordJoin}
                style={{
                  marginBottom: '0.5rem',
                  backgroundColor: '#5865F2',
                  borderColor: '#5865F2',
                }}
              >
                <a
                  href='https://discord.gg/rM7g5Zusmr'
                  target='_blank'
                  rel='noreferrer noopener'
                  style={{ color: 'white', fontWeight: '600' }}
                >
                  Appeal by joining the {getSiteName()} Discord
                </a>
              </DiscordButton>

              <AppealDescription style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
                Or email us for ban appeals (responses may be slower):
              </AppealDescription>
              <ContactMethod onClick={() => copyToClipboard('officialmodsofvs@gmail.com', 'email')}>
                <CopyIcon />
                officialmodsofvs@gmail.com
              </ContactMethod>
            </BanAppealSection>
          ) : (
            <>
              {error && <ErrorMessage>{error}</ErrorMessage>}

              <CaptchaCaption>
                Complete this quick captcha to keep our community safe from bots.
              </CaptchaCaption>
              <CaptchaContainer>
                <ReCAPTCHA
                  sitekey='6Ld-15srAAAAAAVsOeFt1civ4NVk66xVgejkOwYw'
                  onChange={handleCaptchaChange}
                  onExpired={handleCaptchaExpired}
                  onError={handleCaptchaError}
                  theme='dark'
                />
              </CaptchaContainer>

              <ButtonContainer>
                <VerifyButton onClick={handleVerify} disabled={isVerifying}>
                  {isVerifying && <LoadingSpinner />}
                  {isVerifying ? 'Verifying...' : "Let's Go! 🚀"}
                </VerifyButton>
              </ButtonContainer>
            </>
          )}
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
}

export default CaptchaVerificationModal;
